import { FV } from './share';
import { Button, Form, FormItemProps, Popover, Tooltip } from 'antd';
import { EditOutlined, LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { useFormikContext } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import useSmartField from 'hooks/useSmartField';
import { isNoRegionScenario } from '../config';
import { ALL2all } from '../TaskForm/StoreScopesField';
import { uniqBy } from 'lodash-es';
import { getStoreCount } from 'components/StoreScopes';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { useItemSelectContext } from '@knowledge/Activity/ActivityForm/ItemSelectProvider';
import moreSvg from 'views/Knowledge/assets/more.svg';
import { queryItemListByInternalItemIds } from '@knowledge/common/components/ItemSelectV2/api';
import { useRequest } from 'ahooks';
import { Spu, SpuList } from '@knowledge/common/components/SpuBasic';
import { CustomSlateNodeType } from 'components/TemplateEditor/types';
import toLowerCase from 'utils/toLowerCase';
import { useStoreContext } from './StoreContext';

export interface ContentItemListFieldProps {
  region: General.UpperCaseRegion;
  isKnowledge?: boolean;
}

function ContentItemListField(props: ContentItemListFieldProps) {
  const { region, isKnowledge = false } = props;

  const { t } = useTranslate();

  const {
    values: {
      scenarioType,
      platform,
      regionStoreList,
      regionExecuteContexts,
      bilingualExecuteContexts,
    },
  } = useFormikContext<FV>();

  const {
    storeInfo: { storeScopes, storeCountData },
  } = useStoreContext();

  const [touched, setTouched] = useState(false);

  const {
    field: { value, onChange },
    meta: { touched: formTouched },
  } = useSmartField<FV['contentItemList']>('contentItemList', (value) => {
    if (isNoRegionScenario(scenarioType)) {
      if (
        regionExecuteContexts?.some(({ enable, region, executeContext: { content } }) => {
          return (
            enable &&
            content.some((_) => _.type === CustomSlateNodeType.ITEM) &&
            !value?.some((item) => isValidItem(item, region, platform))
          );
        })
      ) {
        return t('knowledge.请选择商品');
      }
    } else if (
      bilingualExecuteContexts[0].content.some((_) => _.type === CustomSlateNodeType.ITEM) &&
      !value?.length
    ) {
      return t('knowledge.请选择商品');
    }
  });

  const finalTouched = touched || formTouched;

  const finalItemList = useMemo(
    () => value?.filter((item) => isValidItem(item, region, platform)),
    [value, platform, region],
  );

  const finalStoreScopes = useMemo(() => {
    if (!isNoRegionScenario(scenarioType)) {
      return storeScopes;
    }

    const regionStoreScopes = ALL2all(regionStoreList);

    if (!regionStoreScopes.length) {
      return [];
    }

    if (regionStoreScopes[0].region === 'all') {
      return storeCountData
        ? [
            {
              platform,
              regionStoreScopes: [
                {
                  region,
                  storeIds: [],
                  storeCount: storeCountData[`${toLowerCase(region)}Cnt`],
                },
              ],
              storeCount: 0,
            },
          ]
        : [];
    }

    return [
      {
        platform,
        regionStoreScopes: regionStoreScopes.filter((_) => _.region === region),
        storeCount: 0,
      },
    ];
  }, [scenarioType, platform, region, regionStoreList, storeScopes, storeCountData]);

  const formItemProps = useMemo<FormItemProps>(() => {
    if (finalTouched && !finalItemList?.length) {
      return {
        validateStatus: 'error',
        help: <Tips>{t('knowledge.请选择商品')}</Tips>,
      };
    }

    if (!finalItemList || !finalItemList.length) {
      return {
        help: <Tips>{t('policy_content_no_item_tips')}</Tips>,
      };
    }

    const itemStoreLength = uniqBy(finalItemList, 'internalStoreId').length;

    const noItemStoreCount = getStoreCount(finalStoreScopes || []) - itemStoreLength;

    if (noItemStoreCount > 0) {
      return {
        validateStatus: 'warning',
        help: <Tips>{t('policy_content_x_store_no_item_tips', { x: noItemStoreCount })}</Tips>,
      };
    }

    return {};
  }, [finalTouched, finalItemList, finalStoreScopes, t]);

  const finalOnChange = (next: FV['contentItemList'] = []) => {
    const others = value?.filter((item) => !isValidItem(item, region, platform));

    onChange([...(others || []), ...next]);
  };

  const refsCurrent = {
    finalItemList,
    finalOnChange,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  const storeScopesUpdatedRef = useRef(false);

  useEffect(() => {
    const { current: storeScopesUpdated } = storeScopesUpdatedRef;

    storeScopesUpdatedRef.current = true;

    if (!storeScopesUpdated) {
      return;
    }

    const storeScopesIncludesItem = ({
      platform,
      region,
      internalStoreId,
    }: Pick<Aide.PolicyContentItem, 'platform' | 'region' | 'internalStoreId'>) => {
      if (!finalStoreScopes) {
        return false;
      }

      const platformStoreScopes = finalStoreScopes.find((_) => _.platform === platform);

      if (!platformStoreScopes) {
        return false;
      }

      const regionStoreScopes = platformStoreScopes.regionStoreScopes.find(
        (_) => _.region === region || _.region === 'all',
      );

      if (!regionStoreScopes) {
        return false;
      }

      const { region: currentRegion, storeIds, storeCount } = regionStoreScopes;

      return (
        currentRegion === 'all' ||
        (!storeIds?.length && storeCount > 0) ||
        storeIds?.includes(internalStoreId)
      );
    };

    const { finalItemList = [], finalOnChange } = refs.current;

    const next = finalItemList.filter(storeScopesIncludesItem);

    if (next.length < finalItemList.length) {
      finalOnChange(next);
    }
  }, [finalStoreScopes]);

  const { show } = useItemSelectContext();

  const selectItems = useMemo(() => {
    return (finalItemList || []).map(({ externalSpuId, internalItemId }) => {
      return {
        externalItemId: externalSpuId,
        internalItemId,
        skuInfo: [],
      };
    }) as unknown as ItemSelectV2.Item[];
  }, [finalItemList]);

  const onShow = () => {
    const getInitialStoreScopes = (): General.StoreScope[] | undefined => {
      if (!finalItemList?.length) {
        return undefined;
      }

      const [platformStoreScope] = finalStoreScopes || [];

      if (!platformStoreScope) {
        return undefined;
      }

      const { platform, regionStoreScopes: [regionStoreScope] = [] } = platformStoreScope;

      if (!regionStoreScope) {
        return undefined;
      }

      const { region, storeIds = [], storeCount } = regionStoreScope;

      if (region === 'all' || (storeIds.length === 0 && storeCount > 0)) {
        return undefined;
      }

      const noItemStoreIds = storeIds.filter((id) =>
        finalItemList.every((item) => item.internalStoreId !== id),
      );

      if (noItemStoreIds.length > 0) {
        return [
          {
            platform,
            regionStoreScopes: [
              {
                region,
                storeIds: noItemStoreIds,
                storeCount: noItemStoreIds.length,
              },
            ],
            storeCount: noItemStoreIds.length,
          },
        ];
      }
    };

    show({
      storeType: 'ALL',
      storeScopes: finalStoreScopes || [],
      value: selectItems,
      skuDisabled: true,
      initialStoreScopes: getInitialStoreScopes(),
      onChange: (value) =>
        finalOnChange(
          value.map((item) => {
            return {
              platform: item.platform,
              region: item.region,
              internalStoreId: Number(item.internalStoreId),
              externalStoreId: item.externalShopId,
              externalSpuId: item.externalItemId,
              internalItemId: item.internalItemId,
            };
          }),
        ),
      onCancel: () => setTimeout(() => setTouched(true)),
    });
  };

  return (
    <Form.Item {...formItemProps}>
      <div>
        <Form.Item className={styles.content} required label={t('recommend_item')}>
          {finalItemList && finalItemList.length ? (
            <div>
              {t('selected_items', { count: finalItemList.length })}
              <Popover content={<ItemPreview ids={finalItemList.map((_) => _.internalItemId)} />}>
                <img width={16} src={moreSvg} alt="more" className="ml-[4px] cursor-pointer" />
              </Popover>
              {isKnowledge ? (
                <Tooltip title={t('policy_content_item_disabled_tips')}>
                  <Button type="link" disabled icon={<EditOutlined />} />
                </Tooltip>
              ) : (
                <Button type="link" icon={<EditOutlined />} onClick={onShow} />
              )}
            </div>
          ) : isKnowledge ? (
            <Tooltip title={t('policy_content_item_disabled_tips')}>
              <Button className="!pl-0" type="link" disabled icon={<PlusOutlined />}>
                {t('aide.添加商品')}
              </Button>
            </Tooltip>
          ) : (
            <Button className="!pl-0" type="link" icon={<PlusOutlined />} onClick={onShow}>
              {t('aide.添加商品')}
            </Button>
          )}
        </Form.Item>
      </div>
    </Form.Item>
  );
}

export function ItemPreview({ ids }: { ids: string[] }) {
  const { data, loading } = useRequest<any, []>(() => queryItemListByInternalItemIds(ids || []), {
    refreshDeps: [ids.join(',')],
  });

  const list = data?.itemList || [];

  return (
    <SpuList>
      {loading ? (
        <LoadingOutlined />
      ) : (
        list.map((spu: any) => {
          spu.skus = spu.skuInfo;
          spu.title = spu.itemName;
          return (
            <li key={spu.spuId}>
              <Spu spu={spu} />
            </li>
          );
        })
      )}
    </SpuList>
  );
}

export function isValidItem(
  item: Pick<Aide.PolicyContentItem, 'platform' | 'region'>,
  region: General.UpperCaseRegion,
  platform?: General.PlatformUpperCase,
) {
  return (item.platform === platform || !platform) && item.region === region;
}

const styles = {
  content: css`
    padding: 8px 12px;
    margin-bottom: 0;
    border: solid 1px var(--fett-border-default);
    .ant-form-item-label {
      min-width: 0 !important;
    }
  `,
};

const Tips = styled.div`
  font-size: 12px;
  margin: 4px 0 8px;
`;

export default ContentItemListField;
